import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import styles from './GenericDialog.styles';
import CrossIcon from 'components/svg/Cross';
import { GIGYA_ACCOUNT_ACTION, GIGYA_MODAL_MESSAGE, GIGYA_SCREENSET } from 'globalConst/gigya-const';
import Button from '../Button';
import ButtonTypes from 'components/Button/Button.const';
import { useDispatch } from 'react-redux';
import { showTalpaGigyaModal } from '../../actions/gigya-actions';

interface GenericDialogProps {
  displayCloseIcon: boolean;
}

const useStyles = createUseStyles(styles, { name: 'GenericDialog' });
const GenericDialog = ({ displayCloseIcon }: GenericDialogProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search] = useState(window.location.search);
  const [isGigyaReady, setIsGigyaReady] = useState(window?.gigya?.isReady);
  const [activationSuccess, setActivationSuccess] = useState<boolean>(false);
  const DialogTitle = activationSuccess
    ? GIGYA_MODAL_MESSAGE.DIALOG_TITLE_SUCCESS
    : GIGYA_MODAL_MESSAGE.DIALOG_TITLE_FAILED;
  const Title = activationSuccess
    ? GIGYA_MODAL_MESSAGE.CONTENT_TITLE_SUCCESS
    : GIGYA_MODAL_MESSAGE.CONTENT_TITLE_FAILED;
  const Text = activationSuccess ? GIGYA_MODAL_MESSAGE.TEXT_SUCCESS : GIGYA_MODAL_MESSAGE.TEXT_FAILED;
  const ButtonTitle = activationSuccess ? GIGYA_MODAL_MESSAGE.BTN_SUCCESS : GIGYA_MODAL_MESSAGE.BTN_FAILED;

  useEffect(() => {
    window.addEventListener('gigyaReady', updateGigyaState);
    return () => {
      window.removeEventListener('gigyaReady', updateGigyaState);
    };
  }, []);

  const updateGigyaState = () => {
    setIsGigyaReady(window.gigya.isReady);
  };

  useEffect(() => {
    if (search.includes(`?account-action=${GIGYA_ACCOUNT_ACTION.ACTIVATION}`)) {
      setIsModalOpen(true);
      setActivationSuccess(search.indexOf('errorCode=0') > -1);
    }
    if (search.includes(`?account-action=${GIGYA_ACCOUNT_ACTION.RESET_PASSWORD}`)) {
      dispatch(showTalpaGigyaModal(GIGYA_SCREENSET.REGISTRATION_LOGIN, GIGYA_SCREENSET.RESET_PASSWORD_SCREEN));
    }
  }, [search, isGigyaReady]);

  const onModalCloseRequest = () => {
    // Optionally do stuff here before closing the modal
    setIsModalOpen(false);
  };

  if (!isModalOpen) {
    return null;
  }

  const openAppropiriateScreenset = () => {
    const appropriateScreenset = activationSuccess
      ? GIGYA_SCREENSET.LOGIN_SCREEN
      : GIGYA_SCREENSET.VERIFICATION_PENDING;

    dispatch(showTalpaGigyaModal(GIGYA_SCREENSET.REGISTRATION_LOGIN, appropriateScreenset));
    setIsModalOpen(false);
  };

  return (
    <div data-testid="generic-dialog" className={classes.modalBackground}>
      <div className={classes.modalBox}>
        <div className={classes.header}>
          <div className={classes.dialogTitle} data-testid="generic-dialog-title">
            {DialogTitle}
          </div>
          <div
            onClick={onModalCloseRequest}
            data-testid="generic-dialog-close-button"
            className={displayCloseIcon ? classes.showCloseIcon : classes.hideCloseIcon}
          >
            <CrossIcon />
          </div>
        </div>
        <div className={classes.dialogContentContainer}>
          <div className={classes.dialogContent}>
            <div className={classes.dialogContentTitle} data-testid="generic-dialog-content-title">
              {Title}
            </div>
            <div className={classes.dialogContentText}>{Text}</div>
            <div className={classes.dialogButtonSection}>
              <Button
                type={ButtonTypes.PRIMARY}
                data-testid="generic-dialog-primary-button"
                text={ButtonTitle}
                className={classes.dialogLoginButton}
                onClick={openAppropiriateScreenset}
              />
              {activationSuccess && (
                <Button
                  type={ButtonTypes.SECONDARY}
                  data-testid="generic-dialog-secondary-button"
                  text="Sluiten"
                  onClick={onModalCloseRequest}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenericDialog;
